import { ExerciseResponse } from '@src/data/exercise-response'
import { NO_EQUIPMENT } from '@src/util/constants'

export type ExerciseFacetCounts = {
  total: number
  facets: {
    [facet: string]: {
      [tag: string]: number
    }
  }
}

function normalizeTagKey (key: string): string {
  return (key || '').toLowerCase() === 'rotate/twist'
    ? 'rotate_twist'
    : (key || '').toLowerCase()
}

export function getCountersForExerciseTagFilter (
  exercises: ExerciseResponse[],
  exerciseTags: any,
  type: string
): number[] {
  const counter = new Map<string, number>()
  const exerciseList = exercises || []

  exerciseList.forEach(exercise => {
    const tags = exercise.exerciseTags || []
    tags.forEach(tag => {
      if (type === tag.type) {
        const tagKey = normalizeTagKey(tag.key)
        counter.set(tagKey, (counter.get(tagKey) || 0) + 1)
      }
    })
  })

  return ((exerciseTags && exerciseTags[type]) || []).map(filter => counter.get(filter) || 0)
}

export function getCountersForEquipmentFilter (
  exercises: ExerciseResponse[],
  equipmentFilterList: string[]
): number[] {
  const counter = new Map<string, number>()
  const exerciseList = exercises || []
  counter.set(NO_EQUIPMENT, 0)

  exerciseList.forEach(exercise => {
    if (!exercise.equipment.length) counter.set(NO_EQUIPMENT, (counter.get(NO_EQUIPMENT) || 0) + 1)
    exercise.equipment.forEach(equipment => {
      counter.set(equipment, (counter.get(equipment) || 0) + 1)
    })
  })

  return (equipmentFilterList || []).map(filter => counter.get(filter) || 0)
}

export function buildExerciseFacetCounts (
  exercises: ExerciseResponse[],
  exerciseTags: any,
  equipmentFilterList: string[],
  tagTypes: string[]
): ExerciseFacetCounts {
  const facets = {}

  const types = tagTypes || []
  types.forEach(type => {
    const values = ((exerciseTags && exerciseTags[type]) || []) as string[]
    const counts = getCountersForExerciseTagFilter(exercises, exerciseTags, type)
    facets[type] = values.reduce((acc, value, index) => {
      acc[value] = counts[index] || 0
      return acc
    }, {})
  })

  const equipmentCounts = getCountersForEquipmentFilter(exercises, equipmentFilterList)
  facets['equipment'] = (equipmentFilterList || []).reduce((acc, value, index) => {
    acc[value] = equipmentCounts[index] || 0
    return acc
  }, {})

  return {
    total: (exercises || []).length,
    facets
  }
}

export function compactExerciseFacetCounts (
  counts: ExerciseFacetCounts,
  maxItemsPerFacet: number = 8
): ExerciseFacetCounts {
  const compactFacets = {}

  Object.keys((counts && counts.facets) || {}).forEach(facet => {
    const values = counts.facets[facet] || {}
    compactFacets[facet] = Object.keys(values)
      .filter(value => values[value] > 0)
      .sort((a, b) => values[b] - values[a])
      .slice(0, maxItemsPerFacet)
      .reduce((acc, value) => {
        acc[value] = values[value]
        return acc
      }, {})
  })

  return {
    total: counts ? counts.total : 0,
    facets: compactFacets
  }
}
